import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useKeycloak } from '@react-keycloak/web';
import { useNavigate, Link } from 'react-router-dom';
import { authenticatedFetch } from '../utils/auth';
import { useAdmin } from '../context/AdminContext';

const NoAccess = () => {
    const { t } = useTranslation();
    const { keycloak } = useKeycloak();
    const navigate = useNavigate();
    const { isAuthorized, refreshAdminStatus } = useAdmin();
    const [showRecoveryOptions, setShowRecoveryOptions] = useState(false);
    
    // Check if user previously had access and has been de-authorized
    useEffect(() => {
        const wasAuthorizedBefore = sessionStorage.getItem('wasAuthorized') === 'true';
        const currentlyAuthorized = localStorage.getItem('isUserAuthorized') === 'true';
        
        // If user previously had access but now doesn't, log them out
        if (wasAuthorizedBefore && !currentlyAuthorized) {
            console.log('User was authorized before but now is not. Logging out...');
            handleLogout();
            return;
        }
        
        // Store current authorization state for future reference
        sessionStorage.setItem('wasAuthorized', currentlyAuthorized ? 'true' : 'false');
        
        // Check authorization on component mount
        checkAuthorization();
    }, [keycloak.authenticated, keycloak.token]);
    
    const checkAuthorization = async () => {
        if (!keycloak.authenticated) {
            return;
        }

        try {
            // First check local storage
            if (localStorage.getItem('isUserAuthorized') === 'true') {
                navigate('/dashboards');
                return;
            }

            // If not in localStorage, check with the server
            const response = await authenticatedFetch(
                '/api/users/check-authorization',
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                },
                keycloak.token
            );

            if (response.ok) {
                const data = await response.json();
                if (data.is_authorized === true) {
                    localStorage.setItem('isUserAuthorized', 'true');
                    navigate('/dashboards');
                    return;
                }
                
                // If API says not authorized, and had access before, log out
                if (data.is_authorized === false && sessionStorage.getItem('wasAuthorized') === 'true') {
                    handleLogout();
                }
            }
        } catch (error) {
            console.error('Authorization check error:', error);
        }
    };
    
    const handleLogout = async () => {
        try {
            // Clear all auth-related storage before logging out
            localStorage.removeItem('isUserAuthorized');
            localStorage.removeItem('adminUsers');
            sessionStorage.removeItem('wasAuthorized');
            
            // Clear any other cached data
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith('auth_') || key.startsWith('user_')) {
                    localStorage.removeItem(key);
                }
            });
            
            // Force logout from Keycloak with redirect back to no-access page
            if (keycloak && keycloak.authenticated) {
                console.log('Logging out from Keycloak...');
                await keycloak.logout({ redirectUri: window.location.origin + '/no-access' });
            }
        } catch (error) {
            console.error('Logout failed:', error);
            
            // If direct logout fails, redirect to login page
            window.location.href = '/login';
        }
    };
    
    const getAdminContact = () => {
        const storedAdmins = localStorage.getItem('adminUsers');
        const adminUsers = storedAdmins ? JSON.parse(storedAdmins) : [];
        return adminUsers[0] || '';
    };

    const handleCheckAccessAgain = async () => {
        try {
            console.log('User requesting fresh access check, forcing logout to get updated token...');
            
            // Clear all auth-related storage
            localStorage.removeItem('isUserAuthorized');
            localStorage.removeItem('adminUsers');
            sessionStorage.removeItem('wasAuthorized');
            
            // Clear any other cached data
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith('auth_') || key.startsWith('user_')) {
                    localStorage.removeItem(key);
                }
            });
            
            // Force logout from Keycloak and redirect back to main page for fresh login
            if (keycloak && keycloak.authenticated) {
                await keycloak.logout({ 
                    redirectUri: window.location.origin + '/dashboards'
                });
            } else {
                // If not authenticated, redirect to login
                window.location.href = '/login';
            }
        } catch (error) {
            console.error('Access check failed:', error);
            // Fallback to manual refresh
            window.location.reload();
        }
    };

    return (
        <div className="no-access-container p-6 max-w-2xl mx-auto mt-10">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold mb-4 text-red-600">Access Restricted</h2>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <p className="text-red-800 font-medium mb-2">
                        You don't have credits to access this feature
                    </p>
                    <p className="text-red-700 text-sm">
                        Please contact support to get access
                    </p>
                </div>
            </div>

            {!showRecoveryOptions ? (
                <div className="text-center">
                    <button
                        onClick={() => setShowRecoveryOptions(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 mb-4"
                    >
                        View Recovery Options
                    </button>
                    <div className="mt-4">
                        <button
                            onClick={handleCheckAccessAgain}
                            className="text-blue-600 hover:text-blue-800 underline text-sm"
                        >
                            Check Access Again
                        </button>
                    </div>
                </div>
            ) : (
                <div className="space-y-6">
                    <h3 className="text-lg font-semibold text-center mb-4">Recovery Options</h3>
                    
                    {/* Option 1: Create New Tenant */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-medium text-gray-800 mb-2">Option 1: Create New Tenant (Become Admin)</h4>
                        <p className="text-gray-600 text-sm mb-3">
                            Set up your own integration and become an admin of your own tenant.
                        </p>
                        <Link
                            to="/integrations"
                            className="inline-block bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm transition-colors"
                        >
                            Set Up Integration
                        </Link>
                    </div>

                    {/* Option 2: Join Existing Tenant */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-medium text-gray-800 mb-2">Option 2: Join Existing Tenant</h4>
                        <p className="text-gray-600 text-sm mb-3">
                            If you know an admin's email from your domain, you can join their tenant.
                        </p>
                        <Link
                            to="/integrations"
                            className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm transition-colors"
                        >
                            Select Tenant
                        </Link>
                    </div>

                    {/* Option 3: Contact Admin */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-medium text-gray-800 mb-2">Option 3: Contact Tenant Admin</h4>
                        <p className="text-gray-600 text-sm mb-3">
                            Request re-authorization from your domain admin.
                        </p>
                        <a 
                            href={`mailto:${getAdminContact()}?subject=Access Request&body=Hi, I need access to the application. My email is ${keycloak.tokenParsed?.email}.`}
                            className="inline-block bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded text-sm transition-colors"
                        >
                            Contact {getAdminContact()}
                        </a>
                    </div>

                    <div className="text-center pt-4 border-t">
                        <button
                            onClick={() => setShowRecoveryOptions(false)}
                            className="text-gray-500 hover:text-gray-700 text-sm"
                        >
                            Hide Options
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NoAccess;